import styles from "../../../../styles/pages/Lead/LeadManagement/Tabs.module.css";
import React, { useState, useCallback, useContext, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { getLeadLoginUrlApi } from "../../../../utils/api/Lead/Action/getLeadLoginUrlApi";
import { deleteLeadApi } from "../../../../utils/api/Lead/deleteLeadApi";
import { hardDeleteLeadApi } from "../../../../utils/api/Lead/hardDeleteLeadApi";
import { getMyAccountApi } from "../../../../utils/api/Account/getMyAccountApi";
import { handleError } from "../../../../utils/custom/errorHandler";
import { Notification } from "../../../../components/UI/Notifications";
import { LoadingCircle } from "../../../../components/UI/Custom/LoadingCircle";
import { LangContext } from "../../../../context/LangContext";
import { ThemeContext } from "../../../../context/ThemeContext";
import { ConfirmationModal } from "../../../../components/UI/Custom/ConfirmationModal";
import { LoginUrlTtlModal } from "../../../../components/Modal/Lead/LoginUrlTtlModal";

const translations = {
    ru: {
        info: "Инфо",
        comments: "Комментарии",
        transactions: "Транзакции",
        verification: "Верификация",
        openDeals: "Открытые сделки",
        tradeHistory: "История торговли",
        history: "История изменений",
        depositMethods: "Методы депозита",
        quickLogin: "Быстрый вход",
        readOnlyLogin: "Read-only login",
        urlMissing: "URL для входа отсутствует в ответе API.",
        copySuccess: "Ссылка для быстрого входа скопирована.",
        readOnlyCopySuccess: "Ссылка для входа только для чтения скопирована.",
        copyError: "Не удалось скопировать ссылку для входа.",
        loginUrlTtlTitle: "Срок действия ссылки",
        quickLoginTtlMessage: "Выберите срок действия ссылки для быстрого входа.",
        readOnlyLoginTtlMessage: "Выберите срок действия ссылки для входа только для чтения.",
        ttlOptions: [
            { value: 1800, label: "30 минут" },
            { value: 3600, label: "1 час" },
            { value: 86400, label: "1 день" },
            { value: 2592000, label: "1 месяц" },
            { value: 0, label: "Бессрочно" },
        ],
        openLoginUrl: "Открыть ссылку",
        copyLoginUrl: "Скопировать ссылку",
        cancel: "Отмена",
        generatingLoginUrl: "Генерация...",
        copyLoginUrlLabel: "Скопировать ссылку для входа",
        deleteLead: "Удалить Лида",
        confirmDeleteTitle: "Подтвердите удаление лида",
        confirmDeleteMessage: (id) => `Вы уверены, что хотите безвозвратно удалить лида #${id}?`,
        confirmDeleteText: "Да, удалить",
        deleting: "Удаление...",
        deleteSuccess: (id) => `Лид #${id} успешно удален.`,
        hardDeleteLead: "Удалить безвозвратно",
        hardDeleteTitle: "Безвозвратное удаление лида",
        hardDeleteMessage: "Это действие полностью удалит лида из системы. Восстановить данные после этого будет невозможно.",
        hardDeleteConfirm: "Удалить навсегда",
        hardDeleteSuccess: "Лид удален безвозвратно.",
        hardDeleteError: "Не удалось безвозвратно удалить лида.",
        chat: "Чат",
    },
    en: {
        info: "Info",
        comments: "Comments",
        transactions: "Transactions",
        verification: "Verification",
        openDeals: "Open deals",
        tradeHistory: "Trade history",
        history: "History",
        depositMethods: "Deposit Methods",
        quickLogin: "Quick login",
        readOnlyLogin: "Read-only login",
        urlMissing: "Login URL is missing from API response.",
        copySuccess: "Quick login URL copied to clipboard.",
        readOnlyCopySuccess: "Read-only login URL copied to clipboard.",
        copyError: "Failed to copy login URL.",
        loginUrlTtlTitle: "Link lifetime",
        quickLoginTtlMessage: "Choose how long the quick login link should remain valid.",
        readOnlyLoginTtlMessage: "Choose how long the read-only login link should remain valid.",
        ttlOptions: [
            { value: 1800, label: "30 minutes" },
            { value: 3600, label: "1 hour" },
            { value: 86400, label: "1 day" },
            { value: 2592000, label: "1 month" },
            { value: 0, label: "Forever" },
        ],
        openLoginUrl: "Open link",
        copyLoginUrl: "Copy link",
        cancel: "Cancel",
        generatingLoginUrl: "Generating...",
        copyLoginUrlLabel: "Copy login link",
        deleteLead: "Delete Lead",
        confirmDeleteTitle: "Confirm Lead Deletion",
        confirmDeleteMessage: (id) => `Are you sure you want to permanently delete Lead #${id}?`,
        confirmDeleteText: "Yes, delete",
        deleting: "Deleting...",
        deleteSuccess: (id) => `Lead #${id} successfully deleted.`,
        hardDeleteLead: "Delete forever",
        hardDeleteTitle: "Hard delete lead",
        hardDeleteMessage: "This action will permanently delete the lead from the system. This cannot be undone.",
        hardDeleteConfirm: "Delete forever",
        hardDeleteSuccess: "Lead was permanently deleted.",
        hardDeleteError: "Failed to permanently delete lead.",
        chat: "Chat",
    },
};

const SUPER_ADMIN_ROLE_NAME = 'Super Admin';

export const Tabs = ({ id, role }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const navigate = useNavigate();

    const [isLoadingLoginUrl, setIsLoadingLoginUrl] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);
    const [loginUrlRequest, setLoginUrlRequest] = useState(null);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentAdmin, setCurrentAdmin] = useState(null);
    const [isHardDeleteConfirmOpen, setIsHardDeleteConfirmOpen] = useState(false);
    const [isHardDeleting, setIsHardDeleting] = useState(false);
    const canHardDeleteLead = role === 'admin' && currentAdmin?.role?.name === SUPER_ADMIN_ROLE_NAME;

    useEffect(() => {
        if (role !== 'admin') {
            setCurrentAdmin(null);
            return;
        }

        let isMounted = true;

        const loadCurrentAdmin = async () => {
            try {
                const accountData = await getMyAccountApi();

                if (isMounted) {
                    setCurrentAdmin(accountData);
                }
            } catch (error) {
                if (isMounted) {
                    setCurrentAdmin(null);
                }
            }
        };

        loadCurrentAdmin();

        return () => {
            isMounted = false;
        };
    }, [role]);

    const fetchLoginUrl = useCallback(async (accessMode, ttl) => {
        setIsLoadingLoginUrl(true);
        setErrors({});
        setSuccessMessage(null);

        try {
            const data = await getLeadLoginUrlApi(id, accessMode, ttl);

            if (data && data.url) {
                return data.url;
            } else {
                handleError({ message: currentTranslation.urlMissing }, setErrors);
                return null;
            }
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            return null;
        } finally {
            setIsLoadingLoginUrl(false);
        }
    }, [id, currentTranslation]);

    const requestLoginUrl = useCallback((accessMode, action) => {
        setErrors({});
        setSuccessMessage(null);
        setLoginUrlRequest({ accessMode, action });
    }, []);

    const handleLoginUrlConfirm = useCallback(async (ttl) => {
        if (!loginUrlRequest) return;

        const { accessMode, action } = loginUrlRequest;
        const url = await fetchLoginUrl(accessMode, ttl);
        if (!url) return;

        if (action === 'open') {
            window.open(url, "_blank", "noopener,noreferrer");
            setLoginUrlRequest(null);
            return;
        }

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);
            } else {

                const textarea = document.createElement("textarea");
                textarea.value = url;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }

            setSuccessMessage(
                accessMode === 'read_only'
                    ? currentTranslation.readOnlyCopySuccess
                    : currentTranslation.copySuccess
            );
            setLoginUrlRequest(null);
        } catch (err) {
            handleError({ message: currentTranslation.copyError }, setErrors);
        }
    }, [fetchLoginUrl, currentTranslation, loginUrlRequest]);

    const handleDeleteClick = () => {
        setErrors({});
        setSuccessMessage(null);
        setIsDeleteConfirmOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setIsDeleteConfirmOpen(false);
    };

    const handleHardDeleteClick = () => {
        setErrors({});
        setSuccessMessage(null);
        setIsHardDeleteConfirmOpen(true);
    };

    const handleCloseHardDeleteConfirmModal = () => {
        if (!isHardDeleting) {
            setIsHardDeleteConfirmOpen(false);
        }
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        setErrors({});
        setSuccessMessage(null);

        try {
            await deleteLeadApi(id);
            setSuccessMessage(currentTranslation.deleteSuccess(id));

            navigate('/lead');
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmModal();
        }
    };

    const handleConfirmHardDelete = async () => {
        if (!canHardDeleteLead || isHardDeleting) {
            return;
        }

        setIsHardDeleting(true);
        setErrors({});
        setSuccessMessage(null);

        try {
            await hardDeleteLeadApi(id);
            setSuccessMessage(currentTranslation.hardDeleteSuccess);

            navigate('/lead');
        } catch (error) {
            handleError(error.response?.data || { message: error.message || currentTranslation.hardDeleteError }, setErrors);
        } finally {
            setIsHardDeleting(false);
            setIsHardDeleteConfirmOpen(false);
        }
    };

    const tabsContainerClass = `${styles.tabsContainer} ${theme === 'dark' ? styles.darkTabsContainer : ''}`;
    const quickLoginClass = `${styles.navItem} ${styles.quickLogin} ${theme === 'dark' ? styles.darkQuickLogin : ''}`;
    const readOnlyLoginClass = `${quickLoginClass} ${styles.readOnlyLogin} ${theme === 'dark' ? styles.darkReadOnlyLogin : ''}`;
    const deleteButtonClass = `${styles.navItem} ${styles.deleteLeadButton} ${theme === 'dark' ? styles.darkDeleteLeadButton : ''}`;
    const hardDeleteButtonClass = `${styles.navItem} ${styles.deleteLeadButton} ${styles.hardDeleteLeadButton} ${theme === 'dark' ? styles.darkDeleteLeadButton : ''}`;


    return (
        <div className={tabsContainerClass}>
            {errors.general && <Notification message={errors.general} type={"error"} />}
            {successMessage && <Notification message={successMessage} type={"success"} />}

            <div className={styles.navigation}>
                <NavLink
                    to={`/lead/change/info/${id}`}
                    className={({ isActive }) =>
                        isActive
                            ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                            : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                    }
                >
                    <InfoOutlinedIcon className={styles.navIcon} />
                    {currentTranslation.info}
                </NavLink>

                {role === 'admin' && (
                    <>
                        <NavLink
                            to={`/lead/change/chat/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <ChatOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.chat}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/deposit-methods-override/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <PaymentOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.depositMethods}
                        </NavLink>

                        <NavLink
                            to={`/lead/change/comments/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <ChatBubbleOutlineOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.comments}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/verification/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <InfoOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.verification}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/transaction/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <AttachMoneyOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.transactions}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/open-deals/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <TrendingUpOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.openDeals}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/trade-history/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <BarChartOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.tradeHistory}
                        </NavLink>
                        <NavLink
                            to={`/lead/change/history/${id}`}
                            className={({ isActive }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active} ${theme === 'dark' ? styles.darkNavItemActive : ''}`
                                    : `${styles.navItem} ${theme === 'dark' ? styles.darkNavItem : ''}`
                            }
                        >
                            <HistoryOutlinedIcon className={styles.navIcon} />
                            {currentTranslation.history}
                        </NavLink>

                        <div
                            className={deleteButtonClass}
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <LoadingCircle size={14} thickness={2} color="#dc3545" />
                            ) : (
                                <>
                                    <DeleteForeverIcon className={styles.navIcon} style={{ color: '#dc3545' }} />
                                    {currentTranslation.deleteLead}
                                </>
                            )}
                        </div>

                        {canHardDeleteLead && (
                            <div
                                className={hardDeleteButtonClass}
                                onClick={handleHardDeleteClick}
                                disabled={isHardDeleting}
                            >
                                {isHardDeleting ? (
                                    <LoadingCircle size={14} thickness={2} color="#dc3545" />
                                ) : (
                                    <>
                                        <DeleteForeverIcon className={styles.navIcon} style={{ color: '#dc3545' }} />
                                        {currentTranslation.hardDeleteLead}
                                    </>
                                )}
                            </div>
                        )}

                        <div
                            className={quickLoginClass}
                            onClick={() => requestLoginUrl(null, 'open')}
                            disabled={isLoadingLoginUrl}
                        >
                            {isLoadingLoginUrl ? (
                                <LoadingCircle size={14} thickness={2} color="#6c63ff" />
                            ) : (
                                <>
                                    <LoginOutlinedIcon className={styles.navIcon} />
                                    {currentTranslation.quickLogin}
                                    <button
                                        type="button"
                                        className={styles.quickLoginCopyButton}
                                        aria-label={currentTranslation.copyLoginUrlLabel}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            requestLoginUrl(null, 'copy');
                                        }}
                                    >
                                        <ContentCopyOutlinedIcon className={styles.quickLoginCopyIcon} />
                                    </button>
                                </>
                            )}
                        </div>

                        <div
                            className={readOnlyLoginClass}
                            onClick={() => requestLoginUrl('read_only', 'open')}
                            disabled={isLoadingLoginUrl}
                        >
                            {isLoadingLoginUrl ? (
                                <LoadingCircle size={14} thickness={2} color="#6c63ff" />
                            ) : (
                                <>
                                    <LockOutlinedIcon className={styles.navIcon} />
                                    {currentTranslation.readOnlyLogin}
                                    <button
                                        type="button"
                                        className={styles.quickLoginCopyButton}
                                        aria-label={currentTranslation.copyLoginUrlLabel}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            requestLoginUrl('read_only', 'copy');
                                        }}
                                    >
                                        <ContentCopyOutlinedIcon className={styles.quickLoginCopyIcon} />
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <ConfirmationModal
                isOpen={isDeleteConfirmOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmDelete}
                title={currentTranslation.confirmDeleteTitle}
                message={currentTranslation.confirmDeleteMessage(id)}
                confirmText={isDeleting ? currentTranslation.deleting : currentTranslation.confirmDeleteText}
            />

            <ConfirmationModal
                isOpen={isHardDeleteConfirmOpen}
                onClose={handleCloseHardDeleteConfirmModal}
                onConfirm={handleConfirmHardDelete}
                title={currentTranslation.hardDeleteTitle}
                message={currentTranslation.hardDeleteMessage}
                confirmText={isHardDeleting ? currentTranslation.deleting : currentTranslation.hardDeleteConfirm}
            />

            <LoginUrlTtlModal
                isOpen={Boolean(loginUrlRequest)}
                onClose={() => !isLoadingLoginUrl && setLoginUrlRequest(null)}
                onConfirm={handleLoginUrlConfirm}
                isLoading={isLoadingLoginUrl}
                theme={theme}
                title={currentTranslation.loginUrlTtlTitle}
                message={loginUrlRequest?.accessMode === 'read_only'
                    ? currentTranslation.readOnlyLoginTtlMessage
                    : currentTranslation.quickLoginTtlMessage}
                options={currentTranslation.ttlOptions}
                confirmText={isLoadingLoginUrl
                    ? currentTranslation.generatingLoginUrl
                    : loginUrlRequest?.action === 'copy'
                        ? currentTranslation.copyLoginUrl
                        : currentTranslation.openLoginUrl}
                cancelText={currentTranslation.cancel}
            />
        </div>
    );
};
